/** Feature groups used for per-user permissions in the demo. */
export const FEATURES = [
  { value: "search", label: "Food search", description: "Semantic + advanced search, link ingredients" },
  { value: "lookup", label: "Lookups", description: "refCode details, barcodes, scan defaults" },
  { value: "reference", label: "Reference data", description: "Tag catalog, concern codes" },
  { value: "text_ai", label: "Text AI tools", description: "Extract foods and structured meals, meal-log actions, ingredient suggestions, look-alikes" },
  { value: "insights", label: "Food insights", description: "Glycemic index/load, ultra-processed rating, rate a food for any goal" },
  { value: "advisor", label: "Nutrition advisor", description: "Free-form advisor chat" },
  { value: "vision", label: "Photo recognition", description: "Meal and plate photos, structured meals from photos, packaged products" },
  { value: "shopping", label: "Shopping links", description: "Shopper-friendly quantities and Instacart affiliate carts" },
  { value: "meal_plans", label: "Meal plans", description: "Meal plan routes (Fast, Macro tuned, Budget, Athletic, Family) and custom EasyAI chains" },
  { value: "gi_coach", label: "GI coach", description: "GLP-1 coach (EasyAI chain)" },
  { value: "grocery_swaps", label: "Grocery swaps", description: "Keto / low-UPF grocery replacements (EasyAI chain)" },
  { value: "cooking", label: "Cooking", description: "Recipes from what's on hand, recreate a dish, recipe makeovers, substitutes" },
  { value: "health", label: "Health checks", description: "Menu coach, food vs. health conditions, daily nutrition review" },
] as const;
export type Feature = (typeof FEATURES)[number]["value"];
export const ALL_FEATURES: Feature[] = FEATURES.map((f) => f.value);

/** A page in the demo. Admins can switch each one off for everyone (Admin → Tools). */
export interface DemoToolDef {
  id: string;
  label: string;
  feature: Feature;
  /** Passio routes the page calls; refused by the proxy while every page using them is off. */
  routes: string[];
}

export const DEMO_TOOLS: DemoToolDef[] = [
  {
    id: "tour",
    label: "Guided tours",
    feature: "text_ai",
    routes: [
      "extractIngredients",
      "invisibleIngredientSuggestions",
      "estimateGlycemicIndexLoad",
      "ultraProcessedFoodRating",
      "genericFoodRating",
      "humanizeShoppingList",
      "convertShoppingListToAffiliateLink",
      "extractIngredientsAutoTyped",
      "visualAlternativeSuggestions",
      "byProductCode",
      "extractFoodAndIngredients",
    ],
  },
  { id: "search", label: "Food search", feature: "search", routes: ["semanticSearch", "advancedSearch", "byRefCode"] },
  { id: "barcode", label: "Barcode", feature: "lookup", routes: ["byProductCode"] },
  { id: "photo", label: "Photo", feature: "vision", routes: ["extractIngredientsAutoTyped", "visionExtractIngredients", "extractPackagedProduct"] },
  { id: "text", label: "Text to foods", feature: "text_ai", routes: ["extractIngredients", "extractFoodAndIngredients", "extractMealLogAction"] },
  { id: "insights", label: "Food insights", feature: "insights", routes: ["estimateGlycemicIndexLoad", "ultraProcessedFoodRating", "genericFoodRating"] },
  { id: "dish", label: "Dish ingredients", feature: "text_ai", routes: ["invisibleIngredientSuggestions", "possibleIngredientSuggestions", "predictIngredients"] },
  { id: "link", label: "Link ingredients", feature: "search", routes: ["linkIngredients"] },
  { id: "alternatives", label: "Look-alikes", feature: "text_ai", routes: ["visualAlternativeSuggestions"] },
  { id: "advisor", label: "Nutrition advisor", feature: "advisor", routes: ["nutritionAdvisor"] },
  {
    id: "meal_plans",
    label: "Meal plans",
    feature: "meal_plans",
    routes: ["mealPlanFast", "mealPlanMacroTuned", "mealPlanBudget", "mealPlanAthletic", "mealPlanFamily", "mealPlanExecution"],
  },
  { id: "gi_coach", label: "GI coach", feature: "gi_coach", routes: [] },
  { id: "grocery_swaps", label: "Grocery swaps", feature: "grocery_swaps", routes: [] },
  { id: "shopping", label: "Shopping cart", feature: "shopping", routes: ["humanizeShoppingList", "convertShoppingListToAffiliateLink"] },
  { id: "reference", label: "Tags & codes", feature: "reference", routes: ["tags", "concernCodes", "scanDefaults"] },
  { id: "nutrition_math", label: "Nutrition math", feature: "lookup", routes: ["advancedSearch", "byRefCode"] },
  { id: "pantry_recipe", label: "What can I cook?", feature: "cooking", routes: ["recipesFromPantry"] },
  { id: "recreate_dish", label: "Recreate a dish", feature: "cooking", routes: ["recipesRecreateDish"] },
  { id: "recipe_makeover", label: "Recipe makeover", feature: "cooking", routes: ["recipesMakeover"] },
  { id: "substitutes", label: "Substitutes", feature: "cooking", routes: ["recipesSubstitutes"] },
  { id: "menu_coach", label: "Menu coach", feature: "health", routes: ["menuCoach"] },
  { id: "condition_check", label: "Condition check", feature: "health", routes: ["foodConditionCheck"] },
  { id: "daily_review", label: "Daily review", feature: "health", routes: ["dailyReview"] },
];

/** Food detail opens from every page's results, so it stays available whatever is switched off. */
const ALWAYS_ON_ROUTES = new Set(["byRefCode"]);

/** Passio route ids that only switched-off pages use. */
export function blockedRouteIds(disabledTools: readonly string[]): Set<string> {
  const off = new Set(disabledTools);
  const used = new Set(DEMO_TOOLS.filter((t) => !off.has(t.id)).flatMap((t) => t.routes));
  return new Set(DEMO_TOOLS.filter((t) => off.has(t.id)).flatMap((t) => t.routes).filter((r) => !used.has(r) && !ALWAYS_ON_ROUTES.has(r)));
}

export type HttpMethod = "GET" | "POST";

export interface PassioRouteDef {
  id: string;
  method: HttpMethod;
  /** Path relative to the products base (`/v2/products`), with `{param}` placeholders. */
  path: string;
  /** `null` = always allowed for signed-in users (e.g. feedback). */
  feature: Feature | null;
  title: string;
  /** Runs on an LLM, so `?model=` picks the model. */
  ai?: boolean;
}

export const PASSIO_ROUTES: PassioRouteDef[] = [
  { id: "semanticSearch", method: "GET", path: "/napi/food/search/semantic", feature: "search", title: "Semantic search", ai: true },
  { id: "advancedSearch", method: "GET", path: "/napi/food/search/advanced", feature: "search", title: "Advanced search" },
  { id: "linkIngredients", method: "POST", path: "/napi/food/search/linkIngredients", feature: "search", title: "Link ingredients", ai: true },
  { id: "byRefCode", method: "GET", path: "/napi/food/search/result/refCode/{refCode}", feature: "lookup", title: "Food by refCode" },
  { id: "byProductCode", method: "GET", path: "/napi/food/productCode/{productCode}", feature: "lookup", title: "Food by barcode" },
  { id: "scanDefaults", method: "GET", path: "/napi/food/scan/defaults", feature: "lookup", title: "Scan defaults" },
  { id: "tags", method: "GET", path: "/napi/food/tags", feature: "reference", title: "Tag catalog" },
  { id: "concernCodes", method: "GET", path: "/napi/food/concernCodes", feature: "reference", title: "Concern codes" },
  { id: "extractIngredients", method: "POST", path: "/napi/tools/extractIngredients", feature: "text_ai", title: "Extract foods from text", ai: true },
  { id: "extractMealLogAction", method: "POST", path: "/napi/tools/extractMealLogAction", feature: "text_ai", title: "Meal-log actions", ai: true },
  { id: "invisibleIngredientSuggestions", method: "POST", path: "/napi/tools/invisibleIngredientSuggestions", feature: "text_ai", title: "Hidden ingredients", ai: true },
  { id: "possibleIngredientSuggestions", method: "POST", path: "/napi/tools/possibleIngredientSuggestions", feature: "text_ai", title: "Possible ingredients", ai: true },
  { id: "predictIngredients", method: "POST", path: "/napi/tools/predictIngredients", feature: "text_ai", title: "Predict ingredients", ai: true },
  { id: "visualAlternativeSuggestions", method: "POST", path: "/napi/tools/visualAlternativeSuggestions", feature: "text_ai", title: "Visual alternatives", ai: true },
  { id: "estimateGlycemicIndexLoad", method: "POST", path: "/napi/tools/estimateGlycemicIndexLoad", feature: "insights", title: "Glycemic index & load", ai: true },
  { id: "ultraProcessedFoodRating", method: "POST", path: "/napi/tools/ultraProcessedFoodRating", feature: "insights", title: "Ultra-processed rating", ai: true },
  { id: "nutritionAdvisor", method: "POST", path: "/napi/tools/nutritionAdvisor", feature: "advisor", title: "Nutrition advisor", ai: true },
  { id: "extractFoodAndIngredients", method: "POST", path: "/napi/tools/extractFoodAndIngredients", feature: "text_ai", title: "Text to a structured meal", ai: true },
  { id: "genericFoodRating", method: "POST", path: "/napi/tools/genericFoodRating", feature: "insights", title: "Rate a food for any goal", ai: true },
  { id: "humanizeShoppingList", method: "POST", path: "/napi/tools/humanizeShoppingList", feature: "shopping", title: "Shopping-friendly quantities", ai: true },
  { id: "visionExtractIngredients", method: "POST", path: "/napi/tools/vision/extractIngredients", feature: "vision", title: "Plate photo to foods", ai: true },
  { id: "visionExtractFoodAndIngredients", method: "POST", path: "/napi/tools/vision/extractFoodAndIngredients", feature: "vision", title: "Photo to a structured meal", ai: true },
  { id: "extractIngredientsAutoTyped", method: "POST", path: "/napi/tools/vision/extractIngredientsAutoTyped", feature: "vision", title: "Photo to foods", ai: true },
  { id: "extractPackagedProduct", method: "POST", path: "/napi/tools/vision/extractPackagedProduct", feature: "vision", title: "Packaged product photo", ai: true },
  { id: "recipesFromPantry", method: "POST", path: "/napi/tools/recipes/fromPantry", feature: "cooking", title: "What can I cook?", ai: true },
  { id: "recipesRecreateDish", method: "POST", path: "/napi/tools/recipes/recreateDish", feature: "cooking", title: "Recreate a dish", ai: true },
  { id: "recipesMakeover", method: "POST", path: "/napi/tools/recipes/makeover", feature: "cooking", title: "Recipe makeover", ai: true },
  { id: "recipesSubstitutes", method: "POST", path: "/napi/tools/recipes/substitutes", feature: "cooking", title: "Ingredient substitutes", ai: true },
  { id: "menuCoach", method: "POST", path: "/napi/tools/menuCoach", feature: "health", title: "Menu coach", ai: true },
  { id: "foodConditionCheck", method: "POST", path: "/napi/tools/foodConditionCheck", feature: "health", title: "Food vs. health conditions", ai: true },
  { id: "dailyReview", method: "POST", path: "/napi/tools/dailyReview", feature: "health", title: "Daily nutrition review", ai: true },
  { id: "convertShoppingListToAffiliateLink", method: "POST", path: "/napi/affiliates/convertShoppingListToAffiliateLink", feature: "shopping", title: "Shopping list to cart", ai: true },
  { id: "mealPlanFast", method: "POST", path: "/napi/tools/mealPlans/fast", feature: "meal_plans", title: "Meal plan: Fast" },
  { id: "mealPlanMacroTuned", method: "POST", path: "/napi/tools/mealPlans/macroTuned", feature: "meal_plans", title: "Meal plan: Macro tuned" },
  { id: "mealPlanBudget", method: "POST", path: "/napi/tools/mealPlans/budget", feature: "meal_plans", title: "Meal plan: Budget" },
  { id: "mealPlanAthletic", method: "POST", path: "/napi/tools/mealPlans/athletic", feature: "meal_plans", title: "Meal plan: Athletic" },
  { id: "mealPlanFamily", method: "POST", path: "/napi/tools/mealPlans/family", feature: "meal_plans", title: "Meal plan: Family" },
  { id: "mealPlanExecution", method: "GET", path: "/napi/tools/mealPlans/executions/{executionId}", feature: "meal_plans", title: "Meal plan progress" },
  { id: "feedback", method: "POST", path: "/napi/feedback", feature: null, title: "Feedback" },
  { id: "feedbackLookup", method: "GET", path: "/napi/feedback/{requestId}", feature: null, title: "Request trace & feedback" },
  { id: "models", method: "GET", path: "/napi/models/available", feature: null, title: "Available models" },
];

const compiled = PASSIO_ROUTES.map((r) => ({
  route: r,
  re: new RegExp(`^${r.path.replace(/[.*+?^$()|[\]\\]/g, "\\$&").replace(/\\?\{[^}]+\\?\}/g, "[^/]+")}$`),
}));

/** Match a method + path (no query string) against the in-scope route list. */
export function matchPassioRoute(method: string, path: string): PassioRouteDef | undefined {
  const m = method.toUpperCase();
  const clean = path.split("?")[0].replace(/\/+$/, "");
  return compiled.find((c) => c.route.method === m && c.re.test(clean))?.route;
}

/**
 * A dynamic AI tool: an EasyAI function an admin published as `POST /napi/tools/{slug}` from
 * EasyAI's Platform Routes page. The demo lists them from EasyAI's registry.
 */
export interface DynamicTool {
  slug: string;
  path: string;
  /** `testing` routes answer only their testers and show only to demo admins. */
  status: "live" | "testing";
  kind: "search" | "text" | "vision" | "kitchen";
  summary: string;
  description: string;
  inputTypes: ("text" | "image" | "video" | "audio")[];
  requestExample: Record<string, unknown> | null;
}

const DYNAMIC_SLUG = /^\/napi\/tools\/([a-z][A-Za-z0-9]{2,47})$/;

/** Id for a dynamic tool in `disabledTools`, route ids and per-route models. */
export const dynamicToolId = (slug: string) => `dyn:${slug}`;

/** The slug when `POST path` could be a dynamic tool (not one of the fixed routes). */
export function dynamicToolSlug(method: string, path: string): string | undefined {
  if (method.toUpperCase() !== "POST" || matchPassioRoute(method, path)) return undefined;
  return DYNAMIC_SLUG.exec(path.split("?")[0].replace(/\/+$/, ""))?.[1];
}

/** Photo tools need the vision feature; the rest count as text AI. */
export const dynamicToolFeature = (t: Pick<DynamicTool, "kind">): Feature => (t.kind === "vision" ? "vision" : "text_ai");

export function dynamicRouteDef(t: DynamicTool): PassioRouteDef {
  return { id: dynamicToolId(t.slug), method: "POST", path: t.path, feature: dynamicToolFeature(t), title: t.summary, ai: true };
}
